/**
 * useInvoiceFormValidation
 *
 * Builds the `validate()` callback that gates submission. Pure consumer of
 * form state values and setters; produces an error map and toggles the
 * error-modal visibility on failure.
 */

import { useCallback } from 'react'

import { addressHasUsage, isValidGSTIN } from '../../../utils/billing-helpers'

import type { InvoiceFormDerived } from './useInvoiceFormDerived'
import type { AddressSnapshot, InvoiceFormState } from './useInvoiceFormState'

export interface InvoiceFormValidationParams {
  state: InvoiceFormState
  derived: InvoiceFormDerived
}

export interface InvoiceFormValidation {
  validate: () => boolean
}

export function useInvoiceFormValidation(
  { state, derived }: InvoiceFormValidationParams,
): InvoiceFormValidation {
  const validate = useCallback((): boolean => {
    const newErrors = collectAllErrors(state, derived)
    state.setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) {
      state.setIsErrorModalVisible(true)
      return false
    }
    return true
  // eslint-disable-next-line react-hooks/exhaustive-deps -- TODO(refactor): verify dependency array intentional
  }, validationDeps(state, derived))

  return { validate }
}

// ─────────────────────────────────────────────────────────────────────────────
// Validation orchestration
// ─────────────────────────────────────────────────────────────────────────────

function collectAllErrors(
  state: InvoiceFormState,
  derived: InvoiceFormDerived,
): Record<string, string> {
  const newErrors: Record<string, string> = {}
  validateParty(newErrors, state)
  validateInvoiceDateAndItems(newErrors, state)
  validateB2BAndB2C(newErrors, state, derived)
  validateBillingAddressRequirement(newErrors, state, derived)
  if (state.invoiceType === 'export') {
    validateExportFields(newErrors, state)
  }
  validateCurrency(newErrors, state)
  validateChangeNotes(newErrors, state)
  return newErrors
}

function validationDeps(state: InvoiceFormState, derived: InvoiceFormDerived): unknown[] {
  return [
    state.selectedPartyId, state.isWalkIn, state.selectedCashAccountId, state.invoiceDate,
    state.lineItems, state.invoiceType, derived.selectedParty, state.selectedShipToAddress,
    state.exportType, state.countryOfDestination, state.shipToType, state.customShipToAddress,
    state.consigneeType, state.consigneeName, state.customConsigneeAddress,
    state.notifyPartyType, state.notifyPartyName, state.customNotifyPartyAddress,
    state.currency, state.exchangeRate, state.isEditingPosted, state.changeNotes,
    derived.allPartyAddresses, state.baseCurrency,
  ]
}

function validateInvoiceDateAndItems(
  newErrors: Record<string, string>,
  state: InvoiceFormState,
): void {
  if (!state.invoiceDate) { newErrors.invoiceDate = 'Invoice date is required' }
  if (state.lineItems.length === 0 || state.lineItems.every((item) => !item.description)) {
    newErrors.items = 'At least one line item is required'
  }
}

function validateBillingAddressRequirement(
  newErrors: Record<string, string>,
  state: InvoiceFormState,
  derived: InvoiceFormDerived,
): void {
  const requiresBilling = !state.isWalkIn
    && (state.invoiceType === 'b2b' || state.invoiceType === 'export')
    && derived.selectedParty
  if (!requiresBilling) { return }
  const hasBillingAddr = derived.allPartyAddresses.some((a) => addressHasUsage(a, 'billing'))
  if (!hasBillingAddr) {
    newErrors.billingAddress = 'Selected party must have a billing address. Please add one.'
  }
}

function validateCurrency(
  newErrors: Record<string, string>,
  state: InvoiceFormState,
): void {
  if (state.currency !== state.baseCurrency
    && (!state.exchangeRate || parseFloat(state.exchangeRate) <= 0)) {
    newErrors.exchangeRate = 'Valid exchange rate is required for foreign currency'
  }
}

function validateChangeNotes(
  newErrors: Record<string, string>,
  state: InvoiceFormState,
): void {
  if (state.isEditingPosted && !state.changeNotes.trim()) {
    newErrors.changeNotes = 'Reason for edit is required when modifying a posted invoice'
  }
}

// =============================================================================
// Private validators
// =============================================================================

function validateParty(
  newErrors: Record<string, string>,
  state: InvoiceFormState,
): void {
  if (!state.isWalkIn && !state.selectedPartyId) {
    newErrors.party = 'Please select a customer'
  }
  if (state.isWalkIn && !state.selectedCashAccountId) {
    newErrors.party = 'Please select a cash/bank account for the walk-in transaction'
  }
}

function validateB2BAndB2C(
  newErrors: Record<string, string>,
  state: InvoiceFormState,
  derived: InvoiceFormDerived,
): void {
  if (state.isWalkIn) { return }
  if (state.invoiceType !== 'b2b' && state.invoiceType !== 'b2c') { return }

  const selectedAddress = state.selectedShipToAddress
    ?? derived.allPartyAddresses.find((a) => addressHasUsage(a, 'billing'))
  if (state.invoiceType === 'b2b') { validateB2BGstin(newErrors, selectedAddress?.gstin); return }
  validateB2CGstin(newErrors, derived.selectedParty?.gstin ?? selectedAddress?.gstin)
}

function validateB2BGstin(newErrors: Record<string, string>, gstin: string | undefined): void {
  if (!gstin || !isValidGSTIN(gstin)) {
    newErrors.party = 'B2B invoices require a billing address with valid GSTIN'
  }
}

function validateB2CGstin(newErrors: Record<string, string>, partyHasGSTIN: string | undefined): void {
  if (partyHasGSTIN) {
    newErrors.party = 'Cannot create B2C invoice for a party with GSTIN. Please use B2B invoice type instead.'
  }
}

function validateExportFields(
  newErrors: Record<string, string>,
  state: InvoiceFormState,
): void {
  if (!state.countryOfDestination) {
    newErrors.countryOfDestination = 'Country of destination is required for export invoices'
  }
  if (state.exportType !== 'goods') { return }
  validateExportShipTo(newErrors, state.shipToType, state.customShipToAddress)
  validateExportConsignee(newErrors, state.consigneeType, state.consigneeName, state.customConsigneeAddress)
  validateExportNotifyParty(newErrors, state.notifyPartyType, state.notifyPartyName, state.customNotifyPartyAddress)
}

function validateExportShipTo(
  newErrors: Record<string, string>,
  shipToType: 'same' | 'party' | 'custom',
  customShipToAddress: AddressSnapshot,
): void {
  if (shipToType !== 'custom') { return }
  if (!customShipToAddress.line1 || !customShipToAddress.city || !customShipToAddress.country) {
    newErrors.shipToAddress = 'Complete ship to address is required'
  }
}

function validateExportConsignee(
  newErrors: Record<string, string>,
  consigneeType: 'same' | 'party' | 'custom',
  consigneeName: string,
  customConsigneeAddress: AddressSnapshot,
): void {
  if (consigneeType !== 'custom') { return }
  if (!consigneeName) {
    newErrors.consigneeName = 'Consignee name is required'
  }
  if (
    !customConsigneeAddress.line1 ||
    !customConsigneeAddress.city ||
    !customConsigneeAddress.country
  ) {
    newErrors.consigneeAddress = 'Complete consignee address is required'
  }
}

function validateExportNotifyParty(
  newErrors: Record<string, string>,
  notifyPartyType: 'same' | 'custom' | 'none',
  notifyPartyName: string,
  customNotifyPartyAddress: AddressSnapshot,
): void {
  if (notifyPartyType !== 'custom') { return }
  if (!notifyPartyName) {
    newErrors.notifyPartyName = 'Notify party name is required'
  }
  if (
    !customNotifyPartyAddress.line1 ||
    !customNotifyPartyAddress.city ||
    !customNotifyPartyAddress.country
  ) {
    newErrors.notifyPartyAddress = 'Complete notify party address is required'
  }
}
